import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextValue {
  showToast: (message: string, type: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

interface ToasterProps {
  position?: string;
  richColors?: boolean;
  closeButton?: boolean;
}

interface ToastOptions {
  duration?: number;
}

let globalShowToast: ((message: string, type: ToastType, duration?: number) => void) | null = null;

function emitToast(message: string, type: ToastType, duration?: number) {
  globalShowToast?.(message, type, duration);
}

type ToastFn = ((message: string, options?: ToastOptions) => void) & {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
};

export const toast: ToastFn = Object.assign(
  (message: string, options?: ToastOptions) => emitToast(message, 'info', options?.duration),
  {
    success: (message: string, options?: ToastOptions) =>
      emitToast(message, 'success', options?.duration),
    error: (message: string, options?: ToastOptions) =>
      emitToast(message, 'error', options?.duration),
    warning: (message: string, options?: ToastOptions) =>
      emitToast(message, 'warning', options?.duration),
    info: (message: string, options?: ToastOptions) =>
      emitToast(message, 'info', options?.duration),
  }
);

interface ToastProviderProps {
  children: React.ReactNode;
}

function useToastHost() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((message: string, type: ToastType, duration: number = 5000) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    globalShowToast = showToast;
    return () => {
      if (globalShowToast === showToast) {
        globalShowToast = null;
      }
    };
  }, [showToast]);

  const viewport = (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );

  return { showToast, viewport };
}

export function ToastProvider({ children }: ToastProviderProps) {
  const { showToast, viewport } = useToastHost();

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {viewport}
    </ToastContext.Provider>
  );
}

export function Toaster(_props: ToasterProps) {
  const { viewport } = useToastHost();
  return viewport;
}

interface ToastProps {
  message: string;
  type: ToastType;
  duration: number;
  onClose: () => void;
}

export function Toast({ message, type, duration, onClose }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const config = {
    success: {
      icon: CheckCircle,
      bg: 'bg-success',
      text: 'text-success-foreground',
    },
    error: {
      icon: AlertCircle,
      bg: 'bg-destructive',
      text: 'text-destructive-foreground',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-warning',
      text: 'text-warning-foreground',
    },
    info: {
      icon: Info,
      bg: 'bg-primary',
      text: 'text-primary-foreground',
    },
  };

  const { icon: Icon, bg, text } = config[type];

  return (
    <div
      data-testid={`toast-${type}`}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg',
        'animate-in slide-in-from-right-full duration-300',
        'min-w-[300px] max-w-md',
        bg,
        text
      )}
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={onClose}
        aria-label="Close toast"
        className="flex-shrink-0 hover:bg-white/20 rounded p-1 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
